"use client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight, Check, Minus, UserCog, School, UserCheck, User,
  ChevronRight, Smartphone, Gauge, FileDown,
  /* Icons, not emoji. The literals that used to sit in these four spots were
     mangled to mojibake by an encoding round-trip and rendered as "" and
     "â-¶" on the live page. */
  Lock, BatteryFull
} from "lucide-react";
import { FEATURE_GROUPS, TOTAL_CATEGORIES, TOTAL_FEATURES } from "./featureData";
import type { RoleKey } from "./featureData";
import { DEFAULT_MOCK, MOCKS, TONE_CLASS } from "./featureMocks";
import type { Narrative } from "./featureNarratives";
import { getScreenshot } from "./featureScreenshots";
import { useLanguage } from "@/contexts/LanguageContext";
import RichText from "./RichText";

// ── Role filter definitions ───────────────────────────────────────────────────────────────
/**
 * The four tabs. The groups behind each come from `role` in featureData — the
 * membership list that used to be duplicated here as an `ids` array is derived
 * now, so a group cannot be in the rail under one tab and in a footer link
 * under another.
 *
 * These are a marketing cut, NOT a copy of anybody's sidebar — see the header of
 * featureData.ts. The tabs used to reproduce the real menus item for item,
 * which handed a competitor the whole navigation tree; they now answer "what
 * does this person get out of it", which is the question a buyer is actually
 * asking. `blurb` replaced a live "N capabilities" count for the same reason:
 * the exact size of each role's menu is a build detail, not a selling point.
 */
/* Icon and colour only — the label and blurb live in `features.roles` in the
   dictionaries, keyed by the same RoleKey. */
const ROLE_FILTERS: Record<RoleKey, { icon: React.ElementType; color: string }> = {
  admin: { icon: UserCog, color: "#0F766E" },
  teacher: { icon: School, color: "#D97706" },
  staff: { icon: UserCheck, color: "#4338CA" },
  student: { icon: User, color: "#DC2626" },
};

/** Groups under a tab, in featureData order. */
const groupsFor = (role: RoleKey) => FEATURE_GROUPS.filter((g) => g.role === role);

const ROLE_ORDER: RoleKey[] = ["admin", "teacher", "staff", "student"];

/**
 * Tabbed feature showcase: a category rail beside a mocked product screen.
 *
 * Earlier passes rendered the catalogue as a wall of cards, which stayed a wall
 * of words however it was styled. Showing the actual interface for each
 * category is what makes it read as a product rather than a list — the window
 * chrome and data shapes deliberately echo the homepage hero so the two feel
 * like the same product.
 */

/** Initials for the row avatars — first letters of the first two words. */
const initials = (s: string) =>
  s
    .replace(/^#\d+\s*·\s*/, "")
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

/** Auto-advance cadence for the mobile rail. */
const AUTOPLAY_MS = 4000;

/**
 * Ceiling on the mockup, shared by the browser window and the phone panel so
 * the two never change size as the visitor moves between categories.
 *
 * The panel column is 9 of 12 inside a 1720px site-container, which works out
 * to ~1222px on a wide monitor — so the desktop captures were being painted at
 * very nearly their native 1220px, and a product shot at 1:1 dominates the page
 * it is meant to illustrate. 1080 pulls it back about a tenth without ever
 * upscaling the file. Below roughly a 1500px viewport the column is already
 * narrower than this and the cap does nothing.
 */
const MOCKUP_WIDTH = "w-full max-w-[1080px]";

/* Opens on the admin view — the buyer evaluating Kaympus is usually the one who
   would live in it. Derived rather than hardcoded to FEATURE_GROUPS[0]: that
   first group is no longer in the admin tab, so seeding from it would leave the
   rail with nothing expanded on first paint. */
const DEFAULT_ROLE: RoleKey = "admin";
const DEFAULT_GROUP = groupsFor(DEFAULT_ROLE)[0] ?? FEATURE_GROUPS[0];

export default function FeatureCatalogue() {
  const { t, tv, tf } = useLanguage();
  /* Both maps are looked up once and indexed by id, because the rail, the
     screenshot alt text and the narrative panel all need the same group's
     copy within one render. */
  const groupCopy = tv<Record<string, { title: string; tagline: string }>>("features.groups");
  const roleCopy = tv<Record<string, { label: string; blurb: string }>>("features.roles");
  const narratives = tv<Record<string, Narrative>>("features.narratives");

  const [activeId, setActiveId] = useState(DEFAULT_GROUP.id);
  const [activeRole, setActiveRole] = useState<RoleKey>(DEFAULT_ROLE);
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const railRef = useRef<HTMLDivElement | null>(null);
  /* Stops the moment the visitor drives the rail themselves — an autoplay that
     keeps yanking the selection out from under a tap is worse than none. */
  const [autoplay, setAutoplay] = useState(true);

  // Filtered groups for the current role
  const visibleGroups = useMemo(() => groupsFor(activeRole), [activeRole]);

  const active = useMemo(
    () => visibleGroups.find((g) => g.id === activeId) ?? visibleGroups[0] ?? FEATURE_GROUPS[0],
    [activeId, visibleGroups]
  );
  /* One screen and one mock per group. There is no per-item selection any more:
     the rail lists groups, and the capabilities live in the "What you get" card
     below, which is prose rather than a control. */
  const mock = MOCKS[active.id] ?? DEFAULT_MOCK;
  /* A real capture wherever we have one, the synthetic panel everywhere else.
     Most groups fall back by design: only two or three hero shots per role are
     published at all. */
  /* The translated title feeds the screenshot's alt text, so a Hindi reader
     gets a Hindi description of the capture. `active.title` is the English
     fallback for a group with no dictionary entry yet. */
  const activeTitle = groupCopy?.[active.id]?.title ?? active.title;
  const shot = useMemo(
    () => getScreenshot(active.id, activeTitle),
    [active.id, activeTitle]
  );
  /* Which frame to draw. A phone capture keeps the device frame even though the
     group's mock may be a desktop one, and vice versa. */
  const showPhone = shot ? shot.isMobile : mock.isMobile;

  const story = narratives?.[active.id];

  const handleCategoryClick = useCallback((id: string) => {
    setAutoplay(false);
    setActiveId(id);
  }, []);

  /* Deep links. The footer links a handful of groups as /features#<group-id>,
     and a group id is not an element id — the rail is a tablist driven by
     state, so there is nothing for the browser (or ScrollToHash) to jump to.
     Resolve the hash here instead: open the tab the group belongs to, select
     it, and bring the section into view. `scroll-mt-24` on the section supplies
     the header offset.

     Three arrival paths, three triggers. Mounting with the hash already set
     covers arriving from another page or on a cold load. `hashchange` and
     `popstate` cover a typed URL and the back button. The click listener covers
     the case the other two miss: a footer link clicked while already standing
     on /features. The App Router moves the hash inside a transition — it fires
     no event, and the URL has often not changed yet when the click handler
     runs — so that path reads the destination off the link's own href instead
     of the address bar. Nothing to poll for and nothing to race.

     The mount delay is a margin for layout settling, not a fight with the
     scroll reset: ConditionalLayout skips its reset whenever a hash is present. */
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    const applyGroup = (id: string, delay: number) => {
      const group = FEATURE_GROUPS.find((g) => g.id === id);
      if (!group) return; // "#features" and friends — ScrollToHash handles those

      setAutoplay(false);
      setActiveRole(group.role);
      setActiveId(group.id);

      clearTimeout(timer);
      timer = setTimeout(() => {
        /* `scroll-mt-24` on the section supplies the header offset. */
        document
          .getElementById("features")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, delay);
    };

    const applyHash = (delay: number) =>
      applyGroup(decodeURIComponent(window.location.hash.slice(1)), delay);

    const onHashChange = () => applyHash(0);
    const onAnchorClick = (e: MouseEvent) => {
      /* Anything that opens elsewhere — middle click, ctrl/cmd click — must
         leave this page where it is. */
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const el = e.target instanceof Element ? e.target : null;
      const link = el?.closest('a[href*="/features#"]');
      const id = link?.getAttribute("href")?.split("#")[1];
      if (id) applyGroup(decodeURIComponent(id), 0);
    };

    applyHash(160);
    window.addEventListener("hashchange", onHashChange);
    window.addEventListener("popstate", onHashChange);
    document.addEventListener("click", onAnchorClick);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("hashchange", onHashChange);
      window.removeEventListener("popstate", onHashChange);
      document.removeEventListener("click", onAnchorClick);
    };
  }, []);

  /* Mobile only: the rail is a horizontal strip there, and most of the
     categories sit off-screen. Stepping through them advertises that there is
     more to swipe to. On lg+ the rail is a full vertical list, so nothing to do. */
  useEffect(() => {
    if (!autoplay) return;
    if (typeof window === "undefined" || !window.matchMedia) return;

    const rail = window.matchMedia("(max-width: 1023px)");
    const still = window.matchMedia("(prefers-reduced-motion: reduce)");

    let timer: ReturnType<typeof setInterval> | undefined;
    const sync = () => {
      clearInterval(timer);
      if (!rail.matches || still.matches) return;
      timer = setInterval(() => {
        setActiveId((cur) => {
          const i = visibleGroups.findIndex((g) => g.id === cur);
          return visibleGroups[(i + 1) % visibleGroups.length].id;
        });
      }, AUTOPLAY_MS);
    };

    sync();
    rail.addEventListener("change", sync);
    still.addEventListener("change", sync);
    return () => {
      clearInterval(timer);
      rail.removeEventListener("change", sync);
      still.removeEventListener("change", sync);
    };
  }, [autoplay, visibleGroups]);

  /* Keep the selected tab in view. Scrolls the rail directly rather than via
     scrollIntoView, which would drag the whole page vertically too — and on
     desktop the rail is now sticky, so dragging the page would slide the panel
     out from under the visitor for no reason.

     Both axes are handled because the rail overflows on a different one per
     breakpoint: horizontally as a swipe strip below lg, vertically as a pinned
     column at lg+. The two are mutually exclusive in practice, but measuring
     both is cheaper than reading a media query and can't fall out of step with
     the classes. One scrollTo carries both — an unset axis in ScrollToOptions
     keeps its current value, so neither undoes the other. */
  useEffect(() => {
    const rail = railRef.current;
    const tab = tabRefs.current[activeId];
    if (!rail || !tab) return;

    const railBox = rail.getBoundingClientRect();
    const tabBox = tab.getBoundingClientRect();
    const to: ScrollToOptions = { behavior: "smooth" };

    if (rail.scrollWidth > rail.clientWidth) {
      to.left = Math.max(
        0,
        rail.scrollLeft +
          (tabBox.left - railBox.left) -
          (rail.clientWidth - tab.offsetWidth) / 2
      );
    }

    if (rail.scrollHeight > rail.clientHeight) {
      to.top = Math.max(
        0,
        rail.scrollTop +
          (tabBox.top - railBox.top) -
          (rail.clientHeight - tab.offsetHeight) / 2
      );
    }

    if (to.left === undefined && to.top === undefined) return;
    rail.scrollTo(to);
  }, [activeId]);

  /* Roving arrow-key navigation, as a tablist is expected to have. */
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const keys = ["ArrowDown", "ArrowRight", "ArrowUp", "ArrowLeft", "Home", "End"];
      if (!keys.includes(e.key)) return;
      e.preventDefault();
      setAutoplay(false);

      const i = visibleGroups.findIndex((g) => g.id === activeId);
      const last = visibleGroups.length - 1;
      let next = i;
      if (e.key === "ArrowDown" || e.key === "ArrowRight") next = i === last ? 0 : i + 1;
      if (e.key === "ArrowUp" || e.key === "ArrowLeft") next = i === 0 ? last : i - 1;
      if (e.key === "Home") next = 0;
      if (e.key === "End") next = last;

      const id = visibleGroups[next].id;
      setActiveId(id);
      tabRefs.current[id]?.focus();
    },
    [activeId, visibleGroups]
  );

  return (
    /* `overflow-x-clip`, NOT `overflow-hidden`. The rail below is `position:
       sticky`, and an `overflow: hidden` ancestor becomes that sticky element's
       scroll container — the rail would then stick to a box that never scrolls,
       i.e. silently do nothing. `clip` still contains the decorative bleed but
       establishes no scroll container, so the rail pins against the viewport. */
    <section
      id="features"
      className="relative w-full scroll-mt-24 bg-white pt-14 sm:pt-20 lg:pt-24 pb-16 sm:pb-24 overflow-x-clip"
    >
      {/* Hairline grid for texture */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-70 [mask-image:linear-gradient(to_bottom,transparent,black_10%,black_90%,transparent)]"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(15,23,42,0.04) 1px, transparent 1px), linear-gradient(to bottom, rgba(15,23,42,0.04) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }}
      />

      <div className="site-container relative">

        {/* ── Header ── */}
        <motion.div
          initial={{ opacity: 0, y: 26 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="max-w-[760px] mb-9 sm:mb-12"
        >
          <h2 className="font-heading font-light text-[27px] min-[380px]:text-[31px] sm:text-[42px] lg:text-[52px] leading-[1.08] tracking-[-0.035em] text-[#0d1117]">
            {/* Grey, not the brand gradient — the accent run here is the
                "nothing it doesn't" counterweight, so it reads as quieter
                than the first half rather than louder. */}
            <RichText
              text={t("features.catalogue.heading")}
              accentClassName="text-slate-400"
              breakClassName="hidden min-[380px]:block"
            />
          </h2>

          {/* Says "the highlights" out loud rather than implying the page is
              the whole product. It isn't, deliberately — and framing the full
              walkthrough as the thing you book is both honest and the better
              call to action. */}
          <p className="mt-5 text-[14.5px] sm:text-[16px] text-slate-500 leading-relaxed max-w-[560px]">
            {tf("features.catalogue.intro", {
              features: TOTAL_FEATURES,
              categories: TOTAL_CATEGORIES,
            })}
          </p>
        </motion.div>

        {/* ── Role filter pills ── */}
        <div className="flex flex-wrap gap-2 mb-7 sm:mb-9">
          {ROLE_ORDER.map((role) => {
            const r = ROLE_FILTERS[role];
            const RoleIcon = r.icon;
            const on = activeRole === role;
            return (
              <button
                key={role}
                onClick={() => {
                  setAutoplay(false);
                  setActiveRole(role);
                  // auto-select first visible category for the new role
                  const first = groupsFor(role)[0];
                  if (first) setActiveId(first.id);
                }}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-sora font-semibold text-[13px] border transition-all duration-200 ${
                  on
                    ? "text-white shadow-md border-transparent"
                    : "bg-white text-slate-500 border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                }`}
                style={on ? { backgroundColor: r.color, borderColor: r.color } : {}}
              >
                <RoleIcon className="w-3.5 h-3.5" strokeWidth={2} />
                {roleCopy?.[role]?.label ?? role}
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-8">

          {/* ── Category rail ── */}
          <div className="relative lg:col-span-4 xl:col-span-3 lg:self-start lg:sticky lg:top-[92px] lg:max-h-[calc(100vh_-_124px)]">
            <div className="rounded-[28px] border border-slate-200/90 bg-white/95 p-3.5 sm:p-4 shadow-[0_20px_50px_-25px_rgba(15,23,42,0.08)] backdrop-blur-sm">
              
              {/* Workspace Header Card at Top */}
              <div className="flex items-center gap-3 p-3 mb-3 bg-gradient-to-r from-indigo-50/90 via-blue-50/60 to-indigo-50/40 border border-indigo-200/60 rounded-2xl">
                <div className="w-10 h-10 rounded-xl bg-indigo-100/90 border border-indigo-200/80 flex items-center justify-center text-[#3D5EE1] font-bold shrink-0 shadow-xs">
                  {(() => {
                    const RoleIcon = ROLE_FILTERS[activeRole].icon;
                    return <RoleIcon className="w-5 h-5 text-[#3D5EE1]" strokeWidth={2} />;
                  })()}
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-sora font-bold text-[13.5px] text-[#0d1117] capitalize leading-tight">
                    {tf("features.catalogue.workspaceTitle", {
                      role: roleCopy?.[activeRole]?.label ?? activeRole,
                    })}
                  </h3>
                  {/* A description, not a count. The live "N capabilities"
                      figure that used to sit here was a read-out of how big
                      that role's real menu is — a number a competitor can size
                      a build against, and one a principal has no use for. */}
                  <p className="text-[11.5px] font-medium text-slate-500 mt-0.5">
                    {roleCopy?.[activeRole]?.blurb}
                  </p>
                </div>
              </div>

              {/* Category rail list */}
              <div
                ref={railRef}
                role="tablist"
                aria-label="Feature categories"
                aria-orientation="vertical"
                onKeyDown={onKeyDown}
                onPointerDown={() => setAutoplay(false)}
                onWheel={() => setAutoplay(false)}
                className="feature-rail no-scrollbar flex gap-2 overflow-x-auto
                           lg:pr-1 lg:pb-3
                           lg:flex-col lg:gap-1.5 lg:overflow-x-hidden lg:overflow-y-auto lg:max-h-[calc(100vh_-_240px)]"
              >
              {visibleGroups.map((g) => {
                const Icon = g.icon;
                const on = g.id === active.id;

                return (
                  <button
                    key={g.id}
                    ref={(el) => {
                      tabRefs.current[g.id] = el;
                    }}
                    role="tab"
                    id={`feat-tab-${g.id}`}
                    aria-selected={on}
                    aria-controls="feat-panel"
                    tabIndex={on ? 0 : -1}
                    onClick={() => handleCategoryClick(g.id)}
                    className={`group relative shrink-0 lg:shrink w-[240px] lg:w-full flex items-center gap-3 rounded-2xl px-3.5 py-3 text-left transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3D5EE1]/40 ${
                      on
                        ? "bg-[#EFF3FE] shadow-xs"
                        : "hover:bg-slate-50 border border-slate-200/50 lg:border-none"
                    }`}
                  >
                    {/* Active left accent bar */}
                    {on && (
                      <span className="absolute left-[3px] top-3 bottom-3 w-[3.5px] bg-[#3D5EE1] rounded-full" />
                    )}

                    <span
                      className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 transition-colors duration-200 ${
                        on ? "bg-white text-[#3D5EE1] shadow-xs" : "bg-slate-100 text-slate-500 group-hover:bg-slate-200/60"
                      }`}
                    >
                      <Icon className="w-4 h-4" strokeWidth={2} />
                    </span>

                    <span className="min-w-0 flex-1">
                      <span
                        className={`block font-sora font-semibold text-[13.5px] leading-tight ${
                          on ? "text-[#0d1117]" : "text-slate-700"
                        }`}
                      >
                        {groupCopy?.[g.id]?.title ?? g.title}
                      </span>
                      {/* The tagline carries the weight the expandable
                          sub-menu used to. It says what the group is for in
                          one line, which is all a rail this narrow can hold
                          legibly — and it is not a menu, which is the point. */}
                      <span
                        className={`block text-[11.5px] leading-snug mt-0.5 ${
                          on ? "text-[#3D5EE1]" : "text-slate-400"
                        }`}
                      >
                        {groupCopy?.[g.id]?.tagline ?? g.tagline}
                      </span>
                    </span>

                    <ChevronRight
                      className={`w-4 h-4 shrink-0 transition-transform duration-200 ${
                        on ? "text-[#3D5EE1] translate-x-0.5" : "text-slate-300 group-hover:text-[#3D5EE1] group-hover:translate-x-0.5"
                      }`}
                    />
                  </button>
                );
              })}
              </div>
            </div>

            {/* Mobile horizontal scroll indicator */}
            <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-white via-white/80 to-transparent flex items-center justify-end pr-1 pointer-events-none lg:hidden">
              <div className="w-6 h-6 rounded-full bg-white shadow-sm border border-slate-200 flex items-center justify-center text-slate-400">
                <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.5} />
              </div>
            </div>
          </div>

          {/* ── Panel ── */}
          <div
            role="tabpanel"
            id="feat-panel"
            aria-labelledby={`feat-tab-${active.id}`}
            className="lg:col-span-8 xl:col-span-9 min-w-0"
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={active.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              >
                {/* App window / Mobile device mockup */}
                {showPhone ? (
                  <div className={`relative ${MOCKUP_WIDTH} rounded-3xl border border-slate-800 bg-gradient-to-br from-[#0B1528] via-[#0F223D] to-[#08101D] p-6 sm:p-10 shadow-[0_30px_70px_-20px_rgba(15,23,42,0.6)] overflow-hidden`}>
                    {/* Ambient radial glow background */}
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(61,94,225,0.18),transparent_70%)] pointer-events-none" />
                    <div className="absolute -top-24 -right-24 w-72 h-72 bg-brand-500/10 rounded-full blur-3xl pointer-events-none" />
                    <div className="absolute -bottom-24 -left-24 w-72 h-72 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />

                    {/* Stage layout with side badges */}
                    <div className="relative z-10 flex flex-col lg:flex-row items-center justify-center gap-6 lg:gap-10">
                      
                      {/* Floating feature pills (left side on desktop) */}
                      <div className="hidden xl:flex flex-col gap-3 shrink-0 max-w-[200px]">
                        <div className="bg-white/10 backdrop-blur-md border border-white/15 rounded-2xl p-3.5 text-white text-[12px] shadow-lg">
                          <div className="flex items-center gap-2 font-sora font-semibold text-sky-300">
                            <Gauge className="w-4 h-4 text-sky-300 shrink-0" />
                            <span>Native Performance</span>
                          </div>
                          <p className="text-[11px] text-slate-300 mt-1 leading-snug">
                            Built for speed with 60fps smooth animations and zero lag.
                          </p>
                        </div>
                        <div className="bg-white/10 backdrop-blur-md border border-white/15 rounded-2xl p-3.5 text-white text-[12px] shadow-lg">
                          <div className="flex items-center gap-2 font-sora font-semibold text-amber-300">
                            <Check className="w-4 h-4 text-amber-300 shrink-0" />
                            <span>Real-time Push</span>
                          </div>
                          <p className="text-[11px] text-slate-300 mt-1 leading-snug">
                            Instant attendance & fee updates delivered in &lt; 2 sec.
                          </p>
                        </div>
                      </div>

                      {/* Smartphone Device Frame */}
                      <div className="w-full max-w-[340px] rounded-[44px] border-[8px] border-slate-900 bg-slate-950 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.8)] overflow-hidden relative ring-1 ring-white/20">
                        
                        {/* Dynamic Island / Notch & Status bar, and the app title
                            header below it. Both are suppressed for a real
                            capture: the phone screenshot already contains the
                            device status bar and the app's own header, so
                            drawing these on top stacks two clocks and two
                            titles inside one bezel. */}
                        {!shot && (
                        <>
                        <div className="bg-slate-950 text-white pt-2.5 pb-2 px-6 flex items-center justify-between text-[11px] font-medium">
                          <span>9:41</span>
                          <div className="w-22 h-4 bg-black rounded-full mx-auto flex items-center justify-center gap-1.5 shadow-inner">
                            <span className="w-2.5 h-2.5 rounded-full bg-slate-800" />
                            <span className="w-1.5 h-1.5 rounded-full bg-blue-900/60" />
                          </div>
                          <span className="flex items-center gap-1 text-[10px] text-slate-300">
                            5G <BatteryFull className="w-3.5 h-3.5" />
                          </span>
                        </div>

                        {/* App Title Header */}
                        <div className="bg-gradient-to-r from-[#1B35AC] via-[#3D5EE1] to-[#172B88] text-white px-4 py-3.5 flex items-center justify-between shadow-md">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <div className="w-7 h-7 rounded-lg bg-white/15 flex items-center justify-center shrink-0 backdrop-blur-xs">
                              <Smartphone className="w-4 h-4 text-brand-200" />
                            </div>
                            <div className="min-w-0">
                              <span className="font-sora font-bold text-[13px] block truncate leading-tight">{mock.screen}</span>
                              <span className="text-[10px] text-brand-200/90 flex items-center gap-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                                Live Sync
                              </span>
                            </div>
                          </div>
                          <span className="text-[9px] bg-white/20 text-white font-bold px-2 py-0.5 rounded-full uppercase tracking-wider shrink-0 backdrop-blur-xs">
                            App
                          </span>
                        </div>
                        </>
                        )}

                        {/* App Content Body — the real capture when we have it,
                            otherwise the synthetic stats/rows below. */}
                        {shot ? (
                          <img
                            src={shot.src}
                            alt={shot.alt}
                            width={390}
                            height={844}
                            loading="lazy"
                            decoding="async"
                            className="block w-full h-auto bg-slate-100"
                          />
                        ) : (
                        <div className="p-3.5 bg-slate-100/90 space-y-3 min-h-[360px]">
                          {/* Quick Stats Cards */}
                          <div className="grid grid-cols-3 gap-2">
                            {mock.stats.map((s) => (
                              <div key={s.label} className="bg-white border border-slate-200/90 rounded-2xl p-2.5 text-center shadow-xs hover:border-brand-400/60 transition-colors">
                                <div className="text-[8px] font-bold text-slate-400 uppercase tracking-wider truncate">{s.label}</div>
                                <div className="text-[12.5px] font-sora font-extrabold text-[#0d1117] mt-0.5 truncate">{s.value}</div>
                                {s.delta && (
                                  <div className="text-[8.5px] font-semibold text-[#3D5EE1] truncate">{s.delta}</div>
                                )}
                              </div>
                            ))}
                          </div>

                          {/* Action / Data Feed Rows */}
                          <div className="space-y-2">
                            {mock.rows.map((r) => (
                              <div key={r.a} className="bg-white border border-slate-200/80 rounded-2xl p-3 flex items-center justify-between gap-2.5 shadow-xs hover:shadow-md transition-all">
                                <div className="min-w-0 flex-1">
                                  <div className="text-[12px] font-sora font-semibold text-[#0d1117] truncate">{r.a}</div>
                                  <div className="text-[10.5px] text-slate-500 truncate mt-0.5">{r.b}</div>
                                </div>
                                <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-[9.5px] font-bold ring-1 ring-inset ${TONE_CLASS[r.tone]}`}>
                                  {r.c}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                        )}

                        {/* Home Indicator */}
                        <div className="bg-slate-100 pb-2 pt-1 flex justify-center">
                          <div className="w-28 h-1 bg-slate-400/80 rounded-full" />
                        </div>
                      </div>

                      {/* Floating feature pills (right side on desktop) */}
                      <div className="hidden xl:flex flex-col gap-3 shrink-0 max-w-[200px]">
                        <div className="bg-white/10 backdrop-blur-md border border-white/15 rounded-2xl p-3.5 text-white text-[12px] shadow-lg">
                          <div className="flex items-center gap-2 font-sora font-semibold text-sky-300">
                            <Check className="w-4 h-4 text-sky-300 shrink-0" />
                            <span>Biometric Security</span>
                          </div>
                          <p className="text-[11px] text-slate-300 mt-1 leading-snug">
                            Protected with FaceID, TouchID, and encrypted tokens.
                          </p>
                        </div>
                        <div className="bg-white/10 backdrop-blur-md border border-white/15 rounded-2xl p-3.5 text-white text-[12px] shadow-lg">
                          <div className="flex items-center gap-2 font-sora font-semibold text-amber-300">
                            <FileDown className="w-4 h-4 text-amber-300 shrink-0" />
                            <span>Instant PDF Exports</span>
                          </div>
                          <p className="text-[11px] text-slate-300 mt-1 leading-snug">
                            Download official report cards and fee receipts instantly.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* No store badges here. They were unlinked divs styled to
                        look like the official App Store / Google Play buttons,
                        so they read as "install it now" on a page where there
                        is nothing to install yet — and clicking them did
                        nothing, which is worse than not offering. Put them back
                        only alongside real store URLs. */}
                  </div>
                ) : (
                  /* Web Desktop Window Preview */
                  <div className={`${MOCKUP_WIDTH} rounded-3xl border border-slate-200/90 bg-white overflow-hidden shadow-[0_24px_60px_-28px_rgba(15,23,42,0.25)]`}>
                    {/* Chrome */}
                    <div className="h-12 sm:h-13 flex items-center gap-3 px-4 sm:px-5 border-b border-slate-100 bg-gradient-to-r from-slate-50 via-slate-100/50 to-slate-50">
                      <div className="flex gap-1.5 shrink-0">
                        <span className="w-3 h-3 rounded-full bg-red-400/90 shadow-xs" />
                        <span className="w-3 h-3 rounded-full bg-yellow-400/90 shadow-xs" />
                        <span className="w-3 h-3 rounded-full bg-green-400/90 shadow-xs" />
                      </div>
                      
                      {/* URL address bar */}
                      <div className="flex-1 max-w-sm mx-auto flex items-center gap-2 rounded-lg bg-white border border-slate-200/80 px-3 py-1 text-[11px] text-slate-500 shadow-xs">
                        <Lock className="w-3 h-3 text-emerald-600 shrink-0" strokeWidth={3} />
                        {/* The role, not the group id. Group ids are internal
                            and were never real routes anyway — printing them
                            here published a route list for no benefit. */}
                        <span className="truncate font-mono">app.kaympus.com/{activeRole}</span>
                      </div>

                      <span className="hidden sm:flex items-center gap-2.5 shrink-0">
                        <span className="w-7 h-7 rounded-full bg-[#3D5EE1] text-white text-[10px] font-bold flex items-center justify-center shadow-xs">
                          RS
                        </span>
                      </span>
                    </div>

                    {/* Body — the real capture when we have one. 1220×854, not
                        the 1440×900 it was shot at. The app's left nav is cut
                        out of the served file, because that sidebar is the same
                        menu tree this page deliberately stopped listing as
                        text; the last 46 rows go too, because the admin shell
                        pins its footer to the viewport and every raw capture
                        ends in a white "Copyright © … - Maple Ridge Public
                        School" band — inside this browser frame that reads as
                        our own footer leaking into the product shot. Cropping
                        in CSS would not do for either — the full image would
                        still be downloadable at its URL.

                        width/height are the file's real pixels, so the browser
                        reserves the right box from the intrinsic ratio and the
                        panel height holds steady while the image loads —
                        switching categories never shifts the rail beside it.
                        They are also why the shape is baked into the filename:
                        see the header of featureScreenshots.ts. */}
                    {shot ? (
                      <div className="bg-slate-50/50 overflow-hidden">
                        <img
                          src={shot.src}
                          alt={shot.alt}
                          width={1220}
                          height={854}
                          loading="lazy"
                          decoding="async"
                          className="block w-full h-auto object-contain object-top rounded-b-2xl"
                        />
                      </div>
                    ) : (
                    <div className="p-4 sm:p-6 bg-slate-50/50">
                      <div className="grid grid-cols-3 gap-3 sm:gap-4">
                        {mock.stats.map((s) => (
                          <div
                            key={s.label}
                            className="rounded-2xl border border-slate-200/80 bg-white px-3.5 py-3 sm:px-5 sm:py-4 shadow-xs hover:shadow-md transition-shadow"
                          >
                            <div className="text-[9.5px] sm:text-[10.5px] font-bold uppercase tracking-[0.14em] text-slate-400 truncate">
                              {s.label}
                            </div>
                            <div className="mt-1.5 font-sora font-extrabold text-[16px] sm:text-[22px] text-[#0d1117] tabular-nums truncate">
                              {s.value}
                            </div>
                            {s.delta && (
                              <div className="mt-1 text-[10px] sm:text-[11.5px] font-semibold text-[#3D5EE1] truncate flex items-center gap-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                {s.delta}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>

                      <div className="mt-4 sm:mt-5 rounded-2xl border border-slate-200/90 overflow-hidden divide-y divide-slate-100 bg-white shadow-xs">
                        {mock.rows.map((r) => (
                          <div
                            key={r.a}
                            className="flex items-center gap-3.5 px-4 py-3 sm:px-5 sm:py-3.5 hover:bg-slate-50/80 transition-colors"
                          >
                            <span className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-brand-50 text-brand-700 text-[10px] sm:text-[11px] font-bold flex items-center justify-center shrink-0 border border-brand-100">
                              {initials(r.a)}
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block text-[12.5px] sm:text-[13.5px] font-sora font-semibold text-[#0d1117] truncate">
                                {r.a}
                              </span>
                              <span className="block text-[11px] sm:text-[12px] text-slate-500 truncate mt-0.5">
                                {r.b}
                              </span>
                            </span>
                            <span
                              className={`shrink-0 rounded-full px-2.5 py-1 sm:px-3 sm:py-1 text-[10px] sm:text-[11px] font-bold ring-1 ring-inset whitespace-nowrap ${TONE_CLASS[r.tone]}`}
                            >
                              {r.c}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                    )}
                  </div>
                )}

                {/* ── What this module actually does ──
                   Guarded rather than assumed: a category added to featureData
                   without a matching narrative should drop this block, not blank
                   the whole page. */}
                {story && (
                  <div className="mt-6 sm:mt-8">
                    {/* Lead — the one-paragraph plain-English answer to "what is
                        this?", set larger than the body copy so it reads as the
                        answer rather than as more list. */}
                    <p className="text-[15px] sm:text-[17px] text-slate-600 leading-[1.72] max-w-[820px]">
                      {story.what}
                    </p>

                    {/* Capabilities beside outcomes. Deliberately two columns,
                        not one merged list: "what you get" and "what changes"
                        are different questions, and a buyer reads them at
                        different moments. */}
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
                      <div className="rounded-2xl border border-slate-200/90 bg-white p-5 sm:p-6">
                        {/* slate-500, not slate-400: at 13px on white the
                            latter (#94a3b8) is 2.56:1, under the 4.5:1 WCAG AA
                            needs. slate-500 is 4.76:1 and still clearly reads
                            as an eyebrow label above the list. */}
                        <h4 className="font-sora font-semibold text-[13px] uppercase tracking-[0.13em] text-slate-500 mb-4">
                          {t("features.catalogue.whatYouGet")}
                        </h4>
                        <ul className="flex flex-col gap-3">
                          {story.provides.map((line) => (
                            <li key={line} className="flex items-start gap-3">
                              <span className="mt-[3px] w-[18px] h-[18px] rounded-md bg-[#2A48C9]/10 text-[#3D5EE1] flex items-center justify-center shrink-0">
                                <Check className="w-[11px] h-[11px]" strokeWidth={3} />
                              </span>
                              <span className="text-[13.5px] sm:text-[14px] text-slate-600 leading-[1.6]">
                                {line}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="rounded-2xl border border-slate-200/90 bg-slate-50/60 p-5 sm:p-6">
                        {/* slate-500, not slate-400: at 13px on white the
                            latter (#94a3b8) is 2.56:1, under the 4.5:1 WCAG AA
                            needs. slate-500 is 4.76:1 and still clearly reads
                            as an eyebrow label above the list. */}
                        <h4 className="font-sora font-semibold text-[13px] uppercase tracking-[0.13em] text-slate-500 mb-4">
                          {t("features.catalogue.howItHelps")}
                        </h4>
                        <ul className="flex flex-col gap-3">
                          {story.helps.map((line) => (
                            <li key={line} className="flex items-start gap-3">
                              <span className="mt-[3px] w-[18px] h-[18px] rounded-md bg-white ring-1 ring-slate-200 text-[#3D5EE1] flex items-center justify-center shrink-0">
                                <ArrowRight className="w-[11px] h-[11px]" strokeWidth={2.6} />
                              </span>
                              <span className="text-[13.5px] sm:text-[14px] text-slate-600 leading-[1.6]">
                                {line}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    {/* The comparison. Put side by side because the claim only
                        lands as a contrast — the Kaympus half on its own is an
                        unfalsifiable boast, and the same sentence beside how the
                        rest of the market is built is a checkable one. */}
                    <div className="mt-4 sm:mt-5 rounded-2xl border border-slate-200/90 overflow-hidden">
                      <div className="px-5 sm:px-6 py-3.5 border-b border-slate-200/90 bg-white">
                        <h4 className="font-sora font-semibold text-[13.5px] sm:text-[14px] text-[#0d1117]">
                          {t("features.catalogue.differentHeading")}
                        </h4>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-200/90">
                        <div className="p-5 sm:p-6 bg-slate-50/70">
                          <div className="flex items-center gap-2 mb-2.5">
                            <span className="w-[18px] h-[18px] rounded-md bg-slate-200/80 text-slate-500 flex items-center justify-center shrink-0">
                              <Minus className="w-[11px] h-[11px]" strokeWidth={3} />
                            </span>
                            <span className="font-sora font-semibold text-[12px] uppercase tracking-[0.13em] text-slate-400">
                              {t("features.catalogue.usualApproach")}
                            </span>
                          </div>
                          <p className="text-[13.5px] sm:text-[14px] text-slate-500 leading-[1.65]">
                            {story.different.others}
                          </p>
                        </div>

                        <div className="p-5 sm:p-6 bg-[#F4FBF9]">
                          <div className="flex items-center gap-2 mb-2.5">
                            <span className="w-[18px] h-[18px] rounded-md bg-[#2A48C9]/15 text-[#3D5EE1] flex items-center justify-center shrink-0">
                              <Check className="w-[11px] h-[11px]" strokeWidth={3} />
                            </span>
                            <span className="font-sora font-semibold text-[12px] uppercase tracking-[0.13em] text-[#3D5EE1]">
                              {t("features.catalogue.withKaympus")}
                            </span>
                          </div>
                          <p className="text-[13.5px] sm:text-[14px] text-slate-600 leading-[1.65]">
                            {story.different.kaympus}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

              </motion.div>
            </AnimatePresence>

            {/* Closing CTA */}
            <div className="mt-7 sm:mt-9 rounded-2xl border border-slate-200/90 bg-slate-50/70 p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-5">
              <div className="min-w-0">
                <p className="font-sora font-semibold text-[16px] sm:text-[18px] leading-tight tracking-[-0.01em] text-[#0d1117]">
                  {tf("features.catalogue.ctaTitle", { count: TOTAL_FEATURES })}
                </p>
                <p className="text-[13px] text-slate-500 mt-1.5 leading-relaxed">
                  {t("features.catalogue.ctaBody")}
                </p>
              </div>
              <a
                href="/contact"
                className="group shrink-0 inline-flex items-center justify-center gap-2 rounded-xl bg-[#3D5EE1] hover:bg-[#1A329B] text-white font-sora font-semibold text-[13.5px] px-6 py-3.5 shadow-[0_10px_26px_-10px_rgba(61,94,225,0.8)] transition-colors duration-300"
              >
                {t("features.catalogue.ctaButton")}
                <ArrowRight
                  size={15}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </a>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}

